import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { usePagination } from "@/hooks/usePagination";
import { getInvoiceStatus } from "@/constants/invoiceMeta";
import { InvoiceSummary } from "@/components/invoices/InvoiceSummary";
import { InvoiceFilters } from "@/components/invoices/InvoiceFilters";
import { InvoiceForm } from "@/components/invoices/InvoiceForm";
import { InvoiceTable } from "@/components/invoices/InvoiceTable";
import { DateRangeProvider, useDateRange } from "@/components/dashboard/DateRangeContext";
import { DateRangeFilter } from "@/components/dashboard/DateRangeFilter";
import { Invoice } from "@/services/common/types";
import * as z from "zod";

// Form validation schema
const invoiceSchema = z.object({
  invoice_number: z.string().optional(),
  title: z.string().min(1, "Invoice title is required"),
  client_id: z.string().min(1, "Please select a client"),
  project_id: z.string().optional(),
  issue_date: z.date(),
  due_date: z.date(),
  status: z.enum(["paid", "not_paid", "overdue"]),
  discount_percentage: z.number().min(0).max(100).optional(),
  products: z.array(z.object({
    product_id: z.string().min(1, "Please select a product"),
    list_price: z.number().min(0, "List price must be positive"),
    invoiced_price: z.number().min(0, "Invoiced price must be positive"),
  })).min(1, "At least one product is required"),
  currency: z.string(),
  amount: z.number(),
});

type InvoiceWithRelations = {
  id: string;
  title?: string;
  client_id: string;
  project_id?: string;
  amount: number;
  currency?: string;
  issue_date?: string;
  due_date?: string;
  paid_date?: string;
  status?: string;
  description?: string;
  created_at: string;
  updated_at: string;
  invoice_number?: string;
  po_number?: string;
  po_type?: string;
  discount_percentage?: number;
  pdf_url?: string;
  org_id?: string;
  clients?: { name: string; id: string };
  projects?: { name: string; id: string };
  invoice_items?: Array<{
    id: string;
    product_id: string;
    quantity: number;
    unit_price: number;
    products?: { id: string; name: string; base_price?: number };
  }>;
};

function InvoicesContent() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { dateRange } = useDateRange();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceWithRelations | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pagination hook
  const {
    currentPage,
    itemsPerPage,
    setCurrentPage,
    getPaginationInfo,
    goToPage,
    goToNextPage,
    goToPrevPage
  } = usePagination({
    totalItems: totalCount,
    initialItemsPerPage: 100
  });

  useEffect(() => {
    fetchInvoices();
    fetchInvoicesSummary();
  }, [currentPage, searchTerm, selectedClient, selectedProject, selectedProduct, selectedStatus, sortBy, sortDirection, dateRange.from, dateRange.to]);

  // Server-side fetch with filtering and pagination
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState<Error | null>(null);
  const [summaryData, setSummaryData] = useState({
    totalAmount: 0,
    paidAmount: 0,
    overdueCount: 0
  });

  const fetchInvoicesSummary = async () => {
    try {
      let query = supabase
        .from("invoices")
        .select("*")
        .gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString());

      // Apply same filters as main query (but no pagination)
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,invoice_number.ilike.%${searchTerm}%`);
      }
      
      if (selectedClient && selectedClient !== "all") {
        query = query.eq('client_id', selectedClient);
      }
      
      if (selectedProject && selectedProject !== "all") {
        query = query.eq('project_id', selectedProject);
      }
      
      if (selectedStatus && selectedStatus !== "all") {
        query = query.eq('status', selectedStatus);
      }

      const { data, error } = await query;
      if (error) throw error;

      const allInvoices = data || [];
      const totalAmount = allInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      const paidAmount = allInvoices.filter(inv => inv.status === "paid" || inv.paid_date).reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      const overdueCount = allInvoices.filter(inv => getInvoiceStatus(inv) === "overdue").length;

      setSummaryData({
        totalAmount,
        paidAmount,
        overdueCount
      });
    } catch (error) {
      console.error('Error fetching invoices summary:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      setInvoicesError(null); // Clear previous errors
      
      // Clear any cached data that might be causing issues
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      
      let query = supabase
        .from("invoices")
        .select(`
          *,
          clients:client_id(id, name),
          projects:project_id(id, name),
          invoice_items(
            id,
            product_id,
            quantity,
            unit_price,
            products(id, name, base_price)
          )
        `, { count: 'exact' });

      // Add date filtering
      query = query.gte('created_at', dateRange.from.toISOString())
        .lte('created_at', dateRange.to.toISOString());

      // Apply sorting
      if (sortBy === 'invoice_number') {
        query = query.order('invoice_number', { ascending: sortDirection === 'asc', nullsFirst: false });
        // Add secondary sort by created_at to handle nulls
        query = query.order('created_at', { ascending: false });
      } else {
        query = query.order(sortBy as any, { ascending: sortDirection === 'asc' });
      }

      // Apply server-side filters
      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,invoice_number.ilike.%${searchTerm}%`);
      }
      
      if (selectedClient && selectedClient !== "all") {
        query = query.eq('client_id', selectedClient);
      }
      
      if (selectedProject && selectedProject !== "all") {
        query = query.eq('project_id', selectedProject);
      }
      
      if (selectedStatus && selectedStatus !== "all") {
        query = query.eq('status', selectedStatus);
      }

      // Apply pagination
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      setInvoices(data as InvoiceWithRelations[] || []);
      setTotalCount(count || 0);
      setInvoicesError(null);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoicesError(error as Error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // Fetch clients for form dropdown
  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch projects for form dropdown
  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, client_id")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch products for form dropdown
  const { data: products } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, name, base_price")
        .order("name");
      if (error) throw error;
      return data;
    }
  });

  const createInvoiceMutation = useMutation({
    mutationFn: async (values: z.infer<typeof invoiceSchema>) => {
      if (!user?.id) throw new Error("User not authenticated");
      
      
      // Check if invoice number already exists
      const { data: existingInvoice, error: checkError } = await supabase
        .from("invoices")
        .select("id")
        .eq("invoice_number", values.invoice_number)
        .maybeSingle();

      if (checkError) throw checkError;
      
      if (existingInvoice) {
        throw new Error(`Invoice number "${values.invoice_number}" already exists. Please use a different number.`);
      }
      
      const invoiceData = {
        invoice_number: values.invoice_number,
        title: values.title,
        client_id: values.client_id,
        amount: values.amount,
        currency: values.currency,
        issue_date: values.issue_date.toISOString().split('T')[0],
        due_date: values.due_date.toISOString().split('T')[0],
        status: values.status === "paid" ? "paid" : values.status === "overdue" ? "overdue" : values.status === "not_paid" ? "not_paid" : "sent",
        paid_date: values.status === "paid" ? new Date().toISOString().split('T')[0] : null,
        project_id: values.project_id || null,
        created_by: user.id,
        discount_percentage: values.discount_percentage || 0,
      };

      const { data: invoiceResult, error: invoiceError } = await supabase
        .from("invoices")
        .insert([invoiceData])
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Create GL posting for sent invoice
      const { error: glError } = await supabase.rpc('create_invoice_gl_posting', {
        invoice_id_param: invoiceResult.id
      });

      if (glError) throw glError;

      // Insert invoice items
      if (values.products && values.products.length > 0) {
        const invoiceItems = values.products.map(product => ({
          invoice_id: invoiceResult.id,
          product_id: product.product_id,
          quantity: 1,
          unit_price: product.invoiced_price,
        }));

        const { error: itemsError } = await supabase
          .from("invoice_items")
          .insert(invoiceItems);

        if (itemsError) throw itemsError;
      }

      return invoiceResult;
    },
    onSuccess: () => {
      fetchInvoices(); // Refresh the invoices list
      queryClient.invalidateQueries({ queryKey: ['sales-overview'] });
      queryClient.refetchQueries({ queryKey: ['sales-overview'] });
      setIsAddModalOpen(false);
      toast({
        title: "Success",
        description: "Invoice created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to create invoice: " + error.message,
        variant: "destructive",
      });
    }
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: async (values: z.infer<typeof invoiceSchema>) => {
      if (!editingInvoice) throw new Error("No invoice selected for editing");
      
      const invoiceData = {
        invoice_number: values.invoice_number || null,
        title: values.title,
        client_id: values.client_id,
        amount: values.amount,
        currency: values.currency,
        issue_date: values.issue_date.toISOString().split('T')[0],
        due_date: values.due_date.toISOString().split('T')[0],
        status: values.status === "paid" ? "paid" : values.status === "overdue" ? "overdue" : values.status === "not_paid" ? "not_paid" : "sent",
        paid_date: values.status === "paid" ? new Date().toISOString().split('T')[0] : null,
        project_id: values.project_id || null,
        updated_by: user?.id,
        discount_percentage: values.discount_percentage || 0,
      };

      const { data: invoiceResult, error: invoiceError } = await supabase
        .from("invoices")
        .update(invoiceData)
        .eq("id", editingInvoice.id)
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Update invoice items - first delete existing ones, then insert new ones
      const { error: deleteItemsError } = await supabase
        .from("invoice_items")
        .delete()
        .eq("invoice_id", editingInvoice.id);

      if (deleteItemsError) throw deleteItemsError;

      if (values.products && values.products.length > 0) {
        const invoiceItems = values.products.map(product => ({
          invoice_id: editingInvoice.id,
          product_id: product.product_id,
          quantity: 1,
          unit_price: product.invoiced_price,
        }));

        const { error: itemsError } = await supabase
          .from("invoice_items")
          .insert(invoiceItems);

        if (itemsError) throw itemsError;
      }

      return invoiceResult;
    },
    onSuccess: () => {
      fetchInvoices(); // Refresh the invoices list
      queryClient.invalidateQueries({ queryKey: ['sales-overview'] });
      queryClient.refetchQueries({ queryKey: ['sales-overview'] });
      setIsEditModalOpen(false);
      setEditingInvoice(null);
      toast({
        title: "Success",
        description: "Invoice updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to update invoice: " + error.message,
        variant: "destructive",
      });
    }
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      // First delete invoice items
      const { error: deleteItemsError } = await supabase
        .from("invoice_items")
        .delete()
        .eq("invoice_id", invoiceId);

      if (deleteItemsError) throw deleteItemsError;

      // Then delete the invoice
      const { error: deleteInvoiceError } = await supabase
        .from("invoices")
        .delete()
        .eq("id", invoiceId);

      if (deleteInvoiceError) throw deleteInvoiceError;
    },
    onSuccess: () => {
      fetchInvoices(); // Refresh the invoices list
      queryClient.invalidateQueries({ queryKey: ['sales-overview'] });
      queryClient.refetchQueries({ queryKey: ['sales-overview'] });
      toast({
        title: "Success",
        description: "Invoice deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to delete invoice: " + error.message,
        variant: "destructive",
      });
    }
  });

  const onSubmit = (values: z.infer<typeof invoiceSchema>) => {
    if (editingInvoice) {
      updateInvoiceMutation.mutate(values);
    } else {
      createInvoiceMutation.mutate(values);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortBy(field);
      setSortDirection(field === 'invoice_number' ? 'desc' : 'desc');
    }
  };

  const handleEdit = (invoice: Invoice) => {
    // Make sure all fields including discount_percentage are passed
    const invoiceWithAllFields = {
      ...invoice,
      discount_percentage: invoice.discount_percentage || 0,
      po_number: invoice.po_number || "",
      po_type: invoice.po_type || "na",
      pdf_url: invoice.pdf_url || ""
    };
    setEditingInvoice(invoiceWithAllFields);
    setIsEditModalOpen(true);
  };

  const handleDelete = (invoice: InvoiceWithRelations) => {
    deleteInvoiceMutation.mutate(invoice.id);
  };

  const handleView = (invoice: Invoice) => {
    // For now, just edit the invoice when viewing
    handleEdit(invoice);
  };

  // Calculate summary metrics from current filtered results - REMOVED (now using summaryData from separate query)

  const { startItem, endItem, totalItems, hasNextPage, hasPrevPage } = getPaginationInfo();

  if (invoicesError) {
    console.error('Invoices error details:', invoicesError);
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load invoices: {invoicesError.message}
          </AlertDescription>
        </Alert>
        <Button 
          onClick={() => {
            setInvoicesError(null);
            fetchInvoices();
          }}
          className="mt-4"
          variant="outline"
        >
          Try Again
        </Button>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Invoices</h1>
        <div className="flex items-center gap-4">
          <DateRangeFilter />
          <Button 
            className="bg-primary hover:bg-primary/90"
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Invoice
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <InvoiceSummary 
        totalAmount={summaryData.totalAmount}
        paidAmount={summaryData.paidAmount}
        overdueCount={summaryData.overdueCount}
        totalInvoices={totalCount}
      />

      {/* Filters */}
      <InvoiceFilters 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedClient={selectedClient}
        onClientChange={setSelectedClient}
        selectedProject={selectedProject}
        onProjectChange={setSelectedProject}
        selectedProduct={selectedProduct}
        onProductChange={setSelectedProduct}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        clients={clients || []}
        projects={projects || []}
        products={products || []}
      />

        {/* Main Table */}
      <InvoiceTable 
        invoices={invoices}
        isLoading={invoicesLoading}
        currentPage={currentPage}
        totalItems={totalCount}
        itemsPerPage={itemsPerPage}
        onNextPage={goToNextPage}
        onPrevPage={goToPrevPage}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      {/* Invoice Form Modal */}
      <InvoiceForm 
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={onSubmit}
        clients={clients || []}
        projects={projects || []}
        products={products || []}
        isSubmitting={createInvoiceMutation.isPending}
      />

      {/* Edit Invoice Form Modal */}
      <InvoiceForm 
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingInvoice(null);
        }}
        onSubmit={onSubmit}
        clients={clients || []}
        projects={projects || []}
        products={products || []}
        isSubmitting={updateInvoiceMutation.isPending}
        editingInvoice={editingInvoice}
      />
    </div>
  );
}

export default function Invoices() {
  return (
    <DateRangeProvider>
      <InvoicesContent />
    </DateRangeProvider>
  );
}